/**
 * Retro letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const retroLettersPack: ToilePack = {
    slug: 'retro-letters',
    category: 'illustration',
    name: 'Retro letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'groovy', label: '70s groovy', subject: 'the word "Groovy" in fat wavy 70s letters with three stacked outline stripes in orange, mustard and brown'},
        {key: 'diner', label: '50s diner', subject: 'the words "Open Late" in a 50s diner script with a long tail swash under the word, on a ribbon with two stars'},
        {key: 'chrome', label: '80s chrome', subject: 'the word "Radical" in 80s chrome letters with a horizon line through them, a sunset gradient and a small sparkle'},
    ],
    styles: [
        {key: 'screenprint', label: 'Screen print', style: 'a two-pass screen print with slight misregistration, subtle halftone texture and softly worn ink', palette: 'The colors follow the era of the lettering, three inks on a cream background.', framing: 'The lettering is centered with a wide even margin.'},
        {key: 'vector', label: 'Flat vector', style: 'clean flat vector lettering with hard edges and smooth gradient-free fills', palette: "The era's colors in a limited palette of five on a solid background.", framing: 'The lettering is centered with a generous margin.'},
        {key: 'sticker', label: 'Die-cut sticker', style: 'a die-cut sticker design with a thick white border around the whole shape and a faint glossy sheen', palette: 'The colors follow the era of the lettering on a plain light gray background.', framing: 'The sticker is centered, well inside the frame.'},
    ],
    directions: ['psychedelic', 'neon pink', 'your name'],
    directionHint: 'try: psychedelic',
    seo: {
        title: 'Retro Letters: 70s Groovy, 50s Diner, 80s Chrome',
        metaDescription: 'Nine retro lettering pieces: the groovy 70s, 50s diner and 80s chrome eras, in screen print, flat vector and die-cut sticker.',
        h1: 'Retro letters: a 70s groovy word, a 50s diner script, an 80s chrome title',
        tagline: 'One word in each era, shown in three print styles.',
        intro: 'Retro letters are lettering from past decades for shirts, posters and party signs. The nine pieces give the word Groovy in fat wavy 70s letters, the words Open Late in a 50s diner script with a tail swash, and Radical in 80s chrome letters with a horizon line. Each era is shown as a two-pass screen print, a flat vector, or a die-cut sticker. Ask for psychedelic, neon pink or your name, and the era lettering is set that way.',
        useCases: [
            'a party sign with a decade theme',
            'a t-shirt design',
            'a poster title',
            'a sticker for a party favor',
        ],
        faq: [
            {q: 'Which words are used?', a: 'Groovy for the 70s, Open Late for the 50s diner and Radical for the 80s.'},
            {q: 'What does the screen print look like?', a: 'Two passes of ink with slight misregistration and a soft halftone texture, like a worn print.'},
            {q: 'Can I get a different word?', a: 'Yes. Ask for your name, psychedelic or neon pink, and the era lettering is set to that.'},
            {q: 'Are these only for shirts?', a: 'No. The sheets also suit posters and party signs.'},
        ],
    },
}
